/**
 * Lista de espera — contrato e textos.
 *
 * A regra de casamento mora no core; aqui ficam a validação da entrada e a
 * forma de escrever a janela em português. As duas coisas são usadas nos dois
 * lados: o servidor monta a mensagem do WhatsApp com o mesmo texto que a tela
 * do cliente mostra, e é isso que evita "das 17:00 às 24:00" aparecer num lugar
 * e "depois das 17h" no outro.
 */

import { DAY_END_MINUTE, isValidWindowRange, type WindowShape, windowShape } from '@barbearia/core';
import { z } from 'zod';
import { MAX_SERVICES_PER_APPOINTMENT } from './catalog.js';
import { calendarDateSchema, idSchema, instantSchema, queryBooleanSchema } from './common.js';

export { DAY_END_MINUTE };

export const waitlistStatusSchema = z.enum([
  'WAITING',
  'BOOKED',
  'EXPIRED',
  'CANCELED_BY_CLIENT',
  'CANCELED_BY_SHOP',
]);

export type WaitlistStatus = z.infer<typeof waitlistStatusSchema>;

export const WAITLIST_STATUS_LABELS: Record<WaitlistStatus, string> = {
  WAITING: 'Aguardando vaga',
  BOOKED: 'Conseguiu horário',
  EXPIRED: 'Expirou',
  CANCELED_BY_CLIENT: 'Desistiu',
  CANCELED_BY_SHOP: 'Removido pela barbearia',
};

/** Quantas esperas uma pessoa pode ter ao mesmo tempo. */
export const MAX_WAITLIST_PER_CLIENT = 3;

/** Validade padrão de uma espera nova. Sem prazo, a lista vira entulho. */
export const DEFAULT_WAITLIST_DAYS = 30;

const minuteOfDaySchema = z.number().int().min(0).max(DAY_END_MINUTE);

/**
 * Os serviços que a pessoa espera fazer.
 *
 * Lista, e não um serviço só, porque quem está na fila por cabelo e barba
 * precisa de um buraco que caiba nos dois — avisá-la de uma vaga de 20 minutos
 * seria chamá-la para uma coisa que não dá tempo de fazer.
 */
const waitlistServicesSchema = z
  .array(idSchema)
  .min(1, 'Escolha ao menos um serviço')
  .max(MAX_SERVICES_PER_APPOINTMENT, 'Serviços demais para um atendimento só')
  .refine((ids) => new Set(ids).size === ids.length, 'O mesmo serviço aparece duas vezes');

const windowFields = {
  /** 0 = domingo … 6 = sábado. Vazio = qualquer dia. */
  weekdays: z.array(z.number().int().min(0).max(6)).max(7).default([]),
  earliestMinute: minuteOfDaySchema.default(0),
  latestMinute: minuteOfDaySchema.default(DAY_END_MINUTE),
  untilDate: calendarDateSchema.optional(),
  note: z.string().trim().max(300).optional(),
};

/**
 * O barbeiro cadastrando o que o cliente pediu no balcão.
 *
 * É a porta principal: a frase que originou esta funcionalidade é dita em voz
 * alta, com o cliente na cadeira. O `clientId` é dele porque quem está com o
 * celular na mão é o barbeiro.
 */
export const createWaitlistSchema = z
  .object({
    clientId: idSchema,
    serviceIds: waitlistServicesSchema,
    /** Ausente = qualquer profissional serve. */
    membershipId: idSchema.optional(),
    ...windowFields,
  })
  .refine(
    (value) => isValidWindowRange(value.earliestMinute, value.latestMinute),
    // A faixa mínima existe para separar espera de agendamento: quem quer
    // exatamente 17h15 não está esperando vaga, está marcando horário.
    {
      message: 'A faixa de horário precisa ter pelo menos 30 minutos',
      path: ['latestMinute'],
    },
  );

export type CreateWaitlistInput = z.input<typeof createWaitlistSchema>;

/** O cliente se colocando na fila pelo app. Não escolhe de quem é a ficha. */
export const requestWaitlistSchema = z
  .object({
    serviceIds: waitlistServicesSchema,
    membershipId: idSchema.optional(),
    ...windowFields,
  })
  .refine((value) => isValidWindowRange(value.earliestMinute, value.latestMinute), {
    message: 'A faixa de horário precisa ter pelo menos 30 minutos',
    path: ['latestMinute'],
  });

/**
 * Marcar de verdade a vaga que a pessoa aceitou.
 *
 * O horário vem do cartão da fila, mas não precisa ser um dos que o app
 * ofereceu: o cliente responde "só consigo 17h30" e o barbeiro digita isso.
 * Por isso `startsAt` é livre, e o encaixe por cima é confirmado como em
 * qualquer outro agendamento — o sistema avisa, não proíbe.
 */
export const bookFromWaitlistSchema = z.object({
  startsAt: instantSchema,
  /** Ausente = o profissional da espera, ou quem está marcando. */
  membershipId: idSchema.optional(),
  acceptConflict: z.boolean().default(false),
});

export const listWaitlistSchema = z.object({
  /** Por padrão só quem ainda espera; o histórico é consulta deliberada. */
  includeClosed: queryBooleanSchema.default(false),
  clientId: idSchema.optional(),
});

/**
 * Onde as vagas são procuradas para quem está na fila.
 *
 * A varredura é de vários dias, e não só de hoje: quem cancela na quarta o
 * horário da sexta abre uma vaga que interessa **agora**. Olhar só o dia
 * corrente esconderia justamente a vaga com tempo de ser preenchida.
 */
export const waitlistMatchQuerySchema = z.object({
  date: calendarDateSchema,
  days: z.coerce.number().int().min(1).max(14).default(7),
  membershipId: idSchema.optional(),
});

// ── Textos ──────────────────────────────────────────────────────────────────

const WEEKDAY_NAMES = [
  'domingo',
  'segunda',
  'terça',
  'quarta',
  'quinta',
  'sexta',
  'sábado',
] as const;

export const WEEKDAY_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const;

const WEEKDAYS_ALL = [0, 1, 2, 3, 4, 5, 6];
const WEEKDAYS_BUSINESS = [1, 2, 3, 4, 5];

function sameSet(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  const sorted = [...a].sort();
  return b.every((value, index) => sorted[index] === value);
}

/** "todo dia", "dias úteis", "terça e quinta". */
export function describeWeekdays(weekdays: readonly number[]): string {
  if (weekdays.length === 0 || sameSet(weekdays, WEEKDAYS_ALL)) return 'qualquer dia';
  if (sameSet(weekdays, WEEKDAYS_BUSINESS)) return 'dias úteis';
  if (sameSet(weekdays, [0, 6])) return 'fim de semana';

  const names = [...weekdays].sort().map((day) => WEEKDAY_NAMES[day] as string);
  if (names.length === 1) return names[0] as string;

  return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
}

function hour(minute: number): string {
  const hours = Math.floor(minute / 60);
  const rest = minute % 60;
  return rest === 0 ? `${hours}h` : `${hours}h${String(rest).padStart(2, '0')}`;
}

/** "depois das 17h", "até o meio-dia", "entre 9h e 12h", "qualquer horário". */
export function describeHours(earliestMinute: number, latestMinute: number): string {
  const shape: WindowShape = windowShape({ earliestMinute, latestMinute });

  switch (shape) {
    case 'ANY':
      return 'qualquer horário';
    case 'AFTER':
      return `depois das ${hour(earliestMinute)}`;
    case 'BEFORE':
      return latestMinute === 12 * 60 ? 'até o meio-dia' : `até as ${hour(latestMinute)}`;
    case 'BETWEEN':
      return `entre ${hour(earliestMinute)} e ${hour(latestMinute)}`;
  }
}

/**
 * A janela inteira em uma frase, do jeito que a pessoa falaria.
 *
 * "dias úteis, depois das 17h" — e não uma tabela de dias com dois campos de
 * hora, que é a mesma informação em formato de formulário.
 */
export function describeWaitlistWindow(window: {
  weekdays: readonly number[];
  earliestMinute: number;
  latestMinute: number;
}): string {
  const days = describeWeekdays(window.weekdays);
  const hours = describeHours(window.earliestMinute, window.latestMinute);

  if (days === 'qualquer dia' && hours === 'qualquer horário') return 'qualquer horário que vagar';
  if (hours === 'qualquer horário') return days;
  if (days === 'qualquer dia') return hours;

  return `${days}, ${hours}`;
}

/** Atalhos de faixa que cobrem quase todo pedido real, em um toque. */
export const WAITLIST_PRESETS: ReadonlyArray<{
  id: string;
  label: string;
  weekdays: number[];
  earliestMinute: number;
  latestMinute: number;
}> = [
  {
    id: 'qualquer',
    label: 'Qualquer vaga',
    weekdays: [],
    earliestMinute: 0,
    latestMinute: DAY_END_MINUTE,
  },
  {
    id: 'fim-de-tarde',
    label: 'Depois das 17h',
    weekdays: [],
    earliestMinute: 17 * 60,
    latestMinute: DAY_END_MINUTE,
  },
  { id: 'manha', label: 'De manhã', weekdays: [], earliestMinute: 0, latestMinute: 12 * 60 },
  {
    id: 'sabado',
    label: 'Sábado',
    weekdays: [6],
    earliestMinute: 0,
    latestMinute: DAY_END_MINUTE,
  },
];

// ── Leitura ─────────────────────────────────────────────────────────────────

export interface WaitlistEntryView {
  id: string;
  status: WaitlistStatus;
  client: {
    id: string;
    name: string;
    phone: string;
    photoUrl: string | null;
    totalNoShows: number;
  };
  /** O principal — o primeiro da lista. */
  service: {
    id: string;
    name: string;
  };
  services: Array<{ id: string; name: string }>;
  /** Nulo = qualquer profissional. */
  staff: { id: string; name: string } | null;
  weekdays: number[];
  earliestMinute: number;
  latestMinute: number;
  fromDate: string;
  untilDate: string;
  note: string | null;
  /** A janela em uma frase, já pronta para a tela e para o WhatsApp. */
  summary: string;
  /**
   * Minutos que esta pessoa leva nestes serviços — o que decide se ela cabe.
   *
   * É recalculado a cada leitura da fila, e não congelado na entrada: entre
   * pedir para ser avisada e uma vaga abrir, o barbeiro pode ter corrigido
   * quanto ela leva.
   */
  durationMinutes: number;
  createdAt: string;
  lastNotifiedAt: string | null;
  notifyCount: number;
  createdByKind: 'CLIENT' | 'STAFF' | 'SYSTEM';
}

/** Uma espera que cabe numa vaga concreta, com os horários que servem. */
export interface WaitlistMatchView {
  entry: WaitlistEntryView;
  /**
   * Instantes UTC de início que servem para esta pessoa, do mais cedo ao mais
   * tarde, ao longo de todos os dias varridos.
   */
  starts: string[];
}
